import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { PaymentRow } from "../../../lib/payments";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

/**
 * The 44px column at the end of a payment row.
 *
 * A payment is not an editable record anywhere in this app - it is created on
 * the invoice and can only be taken back - so the menu holds exactly three
 * things: the jump to the invoice it belongs to, the bank reference (the one
 * value an operator retypes into a statement search), and the deletion.
 */
export function PaymentRowMenu({
  payment,
  canManage,
  onDelete,
}: {
  payment: PaymentRow;
  canManage: boolean;
  onDelete: (row: PaymentRow) => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const navigate = useNavigate();

  async function copyReference() {
    close();
    if (!payment.reference) return;
    try {
      await navigator.clipboard.writeText(payment.reference);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            navigate(`/sales/invoices/${payment.invoiceId}`);
          }}
        >
          <ArrowUpRight className="size-4" />
          {t("sales.payments.openInvoice")}
        </button>
        {payment.reference ? (
          <button type="button" className="crm-mi" onClick={() => void copyReference()}>
            <Copy className="size-4" />
            {t("sales.payments.copyReference")}
          </button>
        ) : null}
        {canManage ? (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onDelete(payment);
              }}
            >
              <Trash2 className="size-4" />
              {t("common.delete")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
